import { reactive } from "vue";

import useConfig from "@/composables/config";
import type { EntityBase } from "@/models/entity-base";

interface AuthData {
  id: number;
  shib_id: string;
  shib_email: string;
  person_id: number;
  last_login: string;
  firstname: string;
  lastname: string;
  fullname: string;
  permissions: Permissions;
  authorization: string[];
  isAuthorizedFor(claims: string[]): boolean;
}

interface Permissions {
  b_a_edit_pers_entries: PermissionTarget[];
  b_c_edit_pers_profile: PermissionTarget[];
  b_d_edit_inst_entries: PermissionTarget[];
  b_e_edit_inst_profile: PermissionTarget[];
  c_a_edit_all_entries: PermissionTarget[];
  c_b_edit_content: PermissionTarget[];
  c_c_edit_config: PermissionTarget[];
  c_d_edit_advanced_auth: PermissionTarget[];
  c_e_edit_admin_auth: PermissionTarget[];
}

export interface PermissionTarget {
  id: number;
  expires: string;
  entity?: EntityBase;
}

class AuthData {
  constructor(user: AuthData) {
    Object.assign(this, user);
  }

  isAuthorizedFor(claims: string[]) {
    return claims.every((p) => this.authorization.includes(p));
  }
}

interface AuthState {
  current: AuthData | null;
  isLoggedIn: boolean;
  isAdmin: boolean;
  isContentAdmin: boolean;
  isConfigAdmin: boolean;
}

const user: AuthState = reactive({
  current: null,
  isLoggedIn: false,
  isAdmin: false,
  isContentAdmin: false,
  isConfigAdmin: false,
});

export default function useUser() {
  function logout() {
    const { config } = useConfig();
    user.current = null;
    window.location.href = `${config.backendPath}/site/logout`;
  }

  async function loadUser() {
    const { config } = useConfig();
    const res = await fetch(`${config.backendPath}/jsonApi/v1/getAuth`);
    const data = (await res.json()) as AuthData;
    if (data.id) {
      user.current = new AuthData(data);
      user.isLoggedIn = true;
      user.isAdmin = user.current.isAuthorizedFor(["accessAdminTools"]);
      user.isContentAdmin = user.current.isAuthorizedFor(["accessCMS"]);
      user.isConfigAdmin = user.current.isAuthorizedFor(["editConfig"]);
    }
  }

  return {
    user,
    logout,
    loadUser,
  };
}
